<template>
  <vs-wrapper v-if="doLineItemsHaveRates" data-test="line-items-summary">
    <vs-wrapper direction="horizontal">
      <v-row>
        <v-col>
          <vs-text bold>Line Items</vs-text>
        </v-col>
        <v-col class="d-flex">
          <v-spacer />
          <vs-text bold data-test="line-items-total">Total: {{ formattedLineItemsTotal }}</vs-text>
          <v-icon
            v-if="lineItemsTotal > 0"
            data-test="line-item-expander"
            class="ml-2"
            @click="isCollapsed = !isCollapsed"
            >{{ isCollapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon
          >
        </v-col>
      </v-row>
    </vs-wrapper>

    <vs-wrapper v-if="!isCollapsed" space="small" class="mt-8">
      <line-item-line
        v-for="lineItem in rolledUpLineItems"
        :key="lineItem.id"
        :line-item="lineItem"
        show-rates
        is-costed
      />
    </vs-wrapper>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import VsText from '@/components/vision/VsText.vue'
import { useLineItemsTotal } from '@/components/jobs/line-items/useLineItemsTotal'
import { LineItem } from '@/models/line-item'
import LineItemLine from '@/components/jobs/line-items/LineItemLine.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { sortBy } from 'lodash'
import { getJobLineItemsQuery } from '@/api/jobs'

const props = defineProps<{
  jobIds: string[]
}>()

const formatter = useCurrencyFormatter()

const isCollapsed = ref<boolean>(true)

const allLineItemQueries = computed(() => props.jobIds.map((id) => getJobLineItemsQuery(id)))
const allLineItems = computed(() => {
  return allLineItemQueries.value
    .filter((queryData) => !queryData.isLoading.value && !queryData.error.value)
    .flatMap((queryData) => queryData.data.value ?? [])
})

const doLineItemsHaveRates = computed(() => !allLineItems.value.some((li) => !li.rate))

const lineItemsTotal = useLineItemsTotal(allLineItems)
const formattedLineItemsTotal = computed(() => formatter.format(lineItemsTotal.value))

const rolledUpLineItemsMap = computed(() =>
  allLineItems.value.reduce((map, l) => {
    const key = `${l.name}-${l.rate ?? 0}`
    const existingItem = map.get(key)
    if (!existingItem) {
      map.set(key, l)
    } else {
      existingItem.quantity = (existingItem.quantity ?? 0) + (l.quantity ?? 0)
      existingItem.matchesRateAgreement =
        existingItem.matchesRateAgreement && l.matchesRateAgreement
    }

    return map
  }, new Map<string, LineItem>()),
)
const rolledUpLineItems = computed(() =>
  sortBy([...rolledUpLineItemsMap.value], (x) => x[0].toLowerCase()).map((x) => x[1]),
)
</script>
